/**
 * Which planned work an approved absence actually takes away (manager redesign, 2026-10-09;
 * coverage audit P1). By instant, not by date: an absence 09–12 touches a 08–16 shift for three
 * hours and a 14–22 shift not at all; an overnight shift is touched by an absence after midnight.
 * Only the affected interval needs cover; the rest of the person's shift stays theirs.
 */
import type { ApiAbsence, ApiShift } from '@/api/data';
import { T } from '@/i18n';
import { oslo, osloISO } from '@/rules';
import { addDays } from '@timefly/core';
import type { Kind } from '../fravaer/AbsenceRange';
import { rangeOf } from '../fravaer/AbsenceRange';

export interface Affected {
  absence: ApiAbsence;
  shift: ApiShift;
  /** the part of the shift inside the absence, as instants */
  from: string;
  to: string;
}

/** The absence as an interval: whole days run from the first midnight to the midnight after the last day (the end date is a day off, ruling 2). */
export function absenceSpan(
  a: Pick<ApiAbsence, 'start_date' | 'end_date'>,
  kind: Kind,
): [number, number] | null {
  const r = rangeOf(a);
  if (!r.from) return null;
  if (kind === 'date')
    return [Date.parse(osloISO(r.from, '00:00')), Date.parse(osloISO(addDays(r.to || r.from, 1), '00:00'))];
  const a0 = Date.parse(a.start_date);
  const b0 = Date.parse(a.end_date);
  return Number.isNaN(a0) || Number.isNaN(b0) || b0 <= a0 ? null : [a0, b0];
}

/** A shift and an absence share time: a shift ending as the absence starts does not (review of #147). */
export const overlaps = (span: [number, number] | null, s: ApiShift): boolean =>
  !!span && Date.parse(s.start_time) < span[1] && Date.parse(s.end_time) > span[0];

/**
 * Each of the absent person's shifts the absence overlaps, cut to the overlap. The shifts carry no
 * link to a replacement, so cover counts as arranged only when it is unmistakable: an open shift
 * for exactly that interval, or someone else's shift for exactly a part of a shift (a partial
 * absence's gap). A colleague whose normal shift happens to have the same hours as a whole absent
 * shift is not taken as cover; that case is listed, and the manager judges it.
 * ponytail: a heuristic until shifts record what they replace; then match on the link.
 */
export function affected(a: ApiAbsence, kind: Kind, shifts: ApiShift[]): Affected[] {
  const span = absenceSpan(a, kind);
  if (!span || a.status !== 'accepted') return [];
  const [a0, b0] = span;
  const out: Affected[] = [];
  for (const s of shifts) {
    if (s.user_id !== a.user_id) continue;
    const from = Math.max(a0, Date.parse(s.start_time));
    const to = Math.min(b0, Date.parse(s.end_time));
    if (to <= from) continue;
    const partial = from !== Date.parse(s.start_time) || to !== Date.parse(s.end_time);
    const covered = shifts.some(
      (o) =>
        o.id !== s.id &&
        o.user_id !== a.user_id &&
        Date.parse(o.start_time) === from &&
        Date.parse(o.end_time) === to &&
        (o.user_id == null || partial),
    );
    if (!covered)
      out.push({ absence: a, shift: s, from: new Date(from).toISOString(), to: new Date(to).toISOString() });
  }
  return out;
}

/** "10:00–14:00", or across midnight "22:00–02:00 (natt til 16.10)". */
export function spanText(x: Pick<Affected, 'from' | 'to'>): string {
  const a = oslo(x.from);
  const b = oslo(x.to);
  if (!a || !b) return '';
  return a.date === b.date
    ? `${a.time}–${b.time}`
    : `${a.time}–${b.time} (${T('natt til {day}', { day: `${Number(b.date.slice(8))}.${Number(b.date.slice(5, 7))}` })})`;
}
